import React, { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { cn } from "@vc/lib/utils"
import { Mic, MicOff, Video, VideoOff, Monitor, Hand, MessageCircle, Users, Subtitles, Maximize, Keyboard } from "lucide-react"

interface ShortcutFeedback {
  id: string
  shortcut: string
  icon: React.ReactNode
  label: string
}

export function ShortcutFeedbackOverlay() {
  const [feedbacks, setFeedbacks] = useState<ShortcutFeedback[]>([])

  const addFeedback = useCallback((shortcut: string, icon: React.ReactNode, label: string) => {
    const id = `${shortcut}-${Date.now()}`
    setFeedbacks((prev) => [...prev, { id, shortcut, icon, label }])
    
    // Remove feedback after 1.5 seconds
    setTimeout(() => {
      setFeedbacks((prev) => prev.filter((f) => f.id !== id))
    }, 1500)
  }, [])

  // Listen for custom keyboard shortcut events
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      // Don't show feedback when typing in input fields
      const target = e.target as HTMLElement
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.contentEditable === "true"
      ) {
        return
      }

      // Don't show feedback when text is selected for copy operations
      const selection = window.getSelection()
      const hasTextSelection = selection && selection.toString().trim().length > 0
      
      if (e.ctrlKey) {
        switch (e.key.toLowerCase()) {
          case "d":
            addFeedback("Ctrl+D", e.target && (e.target as HTMLElement).classList?.contains("mic-off") ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />, "Microphone")
            break
          case "e":
            addFeedback("Ctrl+E", e.target && (e.target as HTMLElement).classList?.contains("video-off") ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />, "Camera")
            break
          case "s":
            addFeedback("Ctrl+S", <Monitor className="h-5 w-5" />, "Screen Share")
            break
          case "h":
            addFeedback("Ctrl+H", <Hand className="h-5 w-5" />, "Hand")
            break
          case "c":
            // Only show feedback if no text is selected
            if (!hasTextSelection) {
              addFeedback("Ctrl+C", <MessageCircle className="h-5 w-5" />, "Chat")
            }
            break
          case "p":
            addFeedback("Ctrl+P", <Users className="h-5 w-5" />, "Participants")
            break
          case "u":
            addFeedback("Ctrl+U", <Subtitles className="h-5 w-5" />, "Captions")
            break
          case "f":
            // Only show feedback for Ctrl+Alt+F (not Ctrl+F which conflicts with find)
            if (e.altKey) {
              addFeedback("Ctrl+Alt+F", <Maximize className="h-5 w-5" />, "Fullscreen")
            }
            break
        }
      } else if (e.key === "?") {
        addFeedback("?", <Keyboard className="h-5 w-5" />, "Shortcuts")
      }
    }

    window.addEventListener("keydown", handleShortcut)
    return () => window.removeEventListener("keydown", handleShortcut)
  }, [addFeedback])

  // Whatever currently owns the fullscreen layer, or null when nothing does.
  const [fullscreenHost, setFullscreenHost] = useState<Element | null>(null)
  useEffect(() => {
    const sync = () => setFullscreenHost(document.fullscreenElement)
    sync()
    document.addEventListener("fullscreenchange", sync)
    document.addEventListener("webkitfullscreenchange", sync)
    return () => {
      document.removeEventListener("fullscreenchange", sync)
      document.removeEventListener("webkitfullscreenchange", sync)
    }
  }, [])

  if (feedbacks.length === 0) return null

  // Render inside whatever is fullscreen, when something is.
  //
  // A fullscreened element is the only thing the browser paints — everything
  // else in the document is simply not on screen. This overlay lives at the
  // meeting root, so while a shared video or document held the fullscreen layer
  // the confirmation for every shortcut was drawn somewhere invisible, and the
  // keyboard looked like it had stopped working even though it had not.
  const host = fullscreenHost ?? document.body

  return createPortal(
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {feedbacks.map((feedback) => (
        <div
          key={feedback.id}
          className={cn(
            "flex items-center gap-3 rounded-lg bg-[#1f2937]/95 px-4 py-3 shadow-xl border border-white/10",
            "animate-in slide-in-from-right-full fade-in duration-200",
            "transition-all duration-300"
          )}
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary">
            {feedback.icon}
          </div>
          <div>
            <p className="text-xs font-medium text-white/60 uppercase tracking-wide">{feedback.shortcut}</p>
            <p className="text-sm font-semibold text-white">{feedback.label}</p>
          </div>
        </div>
      ))}
    </div>,
    host,
  )
}
